if (!customElements.get('upsell-products')) {
    customElements.define('upsell-products', class UpsellProducts extends HTMLElement {
        static selectors = {
            slider: ".js-upsell-products-slider",
            prevButton: ".js-upsell-products-slider-prev",
            nextButton: ".js-upsell-products-slider-next",
            form: ".js-upsell-add-form",
            addButton: ".js-upsell-add-button",
            slide: ".upsell-product.swiper-slide",
            cartRedesign: "#CartDrawer.cart-drawer--redesign"
        }

        // Handles added optimistically; persists across re-renders of the host element.
        // Pruned by filterSlidesByCart once the real cart confirms the line.
        static optimisticAddedHandles = new Set();

        constructor() {
            super();

            this.slider = this.querySelector(UpsellProducts.selectors.slider);
            this.prevButton = this.querySelector(UpsellProducts.selectors.prevButton);
            this.nextButton = this.querySelector(UpsellProducts.selectors.nextButton);
            this.swiper = null;
            this.onCartStateUpdated = this.filterSlidesByCart.bind(this);
            this.onViewportChanged = this.handleViewportChanged.bind(this);
        }

        connectedCallback() {
            if (this.slider) {
                this.swiper = this.initSlider();
                const initVisible = Array.from(this.querySelectorAll(UpsellProducts.selectors.slide))
                    .filter(s => !s.classList.contains('upsell-product--hidden')).length;
                this._syncArrows(initVisible);
                window.addEventListener('resize', this.onViewportChanged);
            }
            this.initForms();
            if (this.syncWithCart) {
                this.filterSlidesByCart();
                document.addEventListener('cart:state-updated', this.onCartStateUpdated);
                document.addEventListener('cart:optimistic-subtotal', this.onCartStateUpdated);

                // cart-drawer.is-empty can toggle outside of the events above (CartOptimist
                // ._render, renderContents, smoothSectionRender). Observe it so the
                // empty-cart layout flips in lock-step with the drawer's empty state.
                const cartDrawer = this.closest('cart-drawer');
                if (cartDrawer) {
                    this._drawerClassObserver = new MutationObserver(this.onCartStateUpdated);
                    this._drawerClassObserver.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
                }
            }
        }

        disconnectedCallback() {
            if (this.slider) {
                window.removeEventListener('resize', this.onViewportChanged);
            }
            if (this.syncWithCart) {
                document.removeEventListener('cart:state-updated', this.onCartStateUpdated);
                document.removeEventListener('cart:optimistic-subtotal', this.onCartStateUpdated);
                if (this._drawerClassObserver) {
                    this._drawerClassObserver.disconnect();
                    this._drawerClassObserver = null;
                }
            }
        }

        get syncWithCart() {
            return this.dataset.syncWithCart === 'true';
        }

        getCartHandles() {
            const cart = window._cartState;
            if (!cart?.items) return new Set();
            return new Set(
                cart.items
                    .map(item => item.handle)
                    .filter(Boolean)
            );
        }

        isCartEmpty() {
            return !!this.closest('cart-drawer')?.classList.contains('is-empty');
        }

        filterSlidesByCart() {
            const cartHandles = this.getCartHandles();
            // Self-prune optimistic handles that the real cart now confirms; otherwise treat them as in-cart.
            UpsellProducts.optimisticAddedHandles.forEach(h => {
                if (cartHandles.has(h)) {
                    UpsellProducts.optimisticAddedHandles.delete(h);
                } else {
                    cartHandles.add(h);
                }
            });
            const slides = Array.from(this.querySelectorAll(UpsellProducts.selectors.slide));
            const cartEmpty = this.isCartEmpty();
            let visibleCount = 0;
            let firstVisibleSeen = false;

            slides.forEach(slide => {
                const handle = slide.dataset.productHandle;
                const effectiveHandle = slide.dataset.effectiveProductHandle;
                const inCart =
                    (handle && cartHandles.has(handle)) ||
                    (effectiveHandle && cartHandles.has(effectiveHandle));

                let hidden = !!inCart;

                if (cartEmpty && !hidden) {
                    if (firstVisibleSeen) hidden = true;
                    else firstVisibleSeen = true;
                }

                slide.classList.toggle('upsell-product--hidden', hidden);
                if (!hidden) visibleCount++;
            });

            this.classList.toggle('upsell-products--empty', visibleCount === 0);

            this._syncArrows(visibleCount);
        }

        _syncArrows(visibleCount) {
            const slidesPerView = this.getSlidesPerView();
            const slideCount = this.usesProductInfoSliderLayout()
                ? this.querySelectorAll(UpsellProducts.selectors.slide).length
                : visibleCount;
            const needsNav = slideCount > slidesPerView;

            const prevEls = Array.from(this.querySelectorAll(UpsellProducts.selectors.prevButton));
            const nextEls = Array.from(this.querySelectorAll(UpsellProducts.selectors.nextButton));
            [...prevEls, ...nextEls].forEach(btn => {
                btn.style.display = needsNav ? '' : 'none';
            });

            if (this.swiper) {
                if (!needsNav && this.swiper.activeIndex > 0) {
                    this.swiper.slideTo(0, 0);
                }
                if (typeof this.swiper.update === 'function') this.swiper.update();
            }
        }

        getSlidesPerView() {
            const isRedesign = this.classList.contains('upsell-products--redesign');
            if (!isRedesign) return 1;
            if (this.usesProductInfoSliderLayout()) return 1;
            if (this.usesCartDrawerLayout()) return 1;

            return window.matchMedia('(max-width: 480px)').matches ? 1 : 2;
        }

        usesProductInfoSliderLayout() {
            return this.classList.contains('upsell-products--redesign') && !!this.closest('.product__info');
        }

        usesCartDrawerLayout() {
            return this.classList.contains('upsell-products--redesign') && !!this.closest('.cart-drawer');
        }

        handleViewportChanged() {
            const visibleCount = Array.from(this.querySelectorAll(UpsellProducts.selectors.slide))
                .filter(s => !s.classList.contains('upsell-product--hidden')).length;

            this._syncArrows(visibleCount);
        }

        initSlider() {
            const isRedesign = this.classList.contains('upsell-products--redesign');
            const useProductInfoSliderLayout = this.usesProductInfoSliderLayout();
            const useCartDrawerLayout = this.usesCartDrawerLayout();
            const prevEls = Array.from(this.querySelectorAll(UpsellProducts.selectors.prevButton));
            const nextEls = Array.from(this.querySelectorAll(UpsellProducts.selectors.nextButton));

            const swiper = new Swiper(this.slider, {
                slidesPerView: 1,
                spaceBetween: 16,
                navigation: {
                    prevEl: prevEls.length > 1 ? prevEls : (prevEls[0] || null),
                    nextEl: nextEls.length > 1 ? nextEls : (nextEls[0] || null)
                },
                ...(useCartDrawerLayout ? {} : isRedesign && !useProductInfoSliderLayout ? {
                    breakpoints: {
                        481: { slidesPerView: 2, spaceBetween: 16 }
                    }
                } : {
                    breakpoints: {
                        381: { slidesPerView: 1, spaceBetween: 16 }
                    }
                }),
                noSwiping: true,
                noSwipingClass: 'swiper-no-swiping'
            });

            return swiper;
        }

        initForms() {
            this.querySelectorAll(UpsellProducts.selectors.form).forEach(form => {
                form.addEventListener('submit', (e) => {
                    e.preventDefault();
                    this.handleAddToCart(form);
                });
            });
        }

        async handleAddToCart(form) {
            const button = form.querySelector(UpsellProducts.selectors.addButton);
            const label = button.querySelector('.upsell-product__add-button-label');
            const isSyncedHost = !!form.closest('[data-sync-with-cart="true"]');
            const slide = form.closest('.upsell-product');
            const optimisticHandles = slide
                ? [slide.dataset.productHandle, slide.dataset.effectiveProductHandle].filter(Boolean)
                : [];

            if (button.disabled) return;

            button.classList.add('is-added');
            button.disabled = true;
            if (label) label.textContent = 'Added';


            const productData = this.getProductDataFromSlide(slide);

            try {
                const formData = new FormData(form);

                const igItemProperty = window.igCustomData?.itemProperty;
                if (igItemProperty) {
                    const [key, value] = Object.entries(igItemProperty)[0];
                    formData.append(`properties[${key}]`, value);
                }

                const cartDrawer = document.querySelector('cart-drawer');

                if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
                    const sectionIds = cartDrawer.getSectionsToRender().map(s => s.section || s.id);
                    formData.append('sections', sectionIds.join(','));
                    formData.append('sections_url', window.location.pathname);
                }

                const sellingPlan = formData.get('selling_plan');
                if (sellingPlan && this.hasSubscriptionInCart()) {
                    window.dispatchEvent(new CustomEvent('notification-modal:show', {
                        detail: { message: window.cartStrings?.alreadyIncludedSubscriptionError }
                    }));
                    button.classList.remove('is-added');
                    button.disabled = false;
                    if (label) label.textContent = 'Add';
                    if (slide && isSyncedHost) {
                        slide.classList.add('upsell-product--hidden');
                        if (this.swiper && typeof this.swiper.update === 'function') this.swiper.update();
                    }
                    return;
                }

                window._CART_REQUESTS = (window._CART_REQUESTS || 0) + 1;
                window._CART_ADD_IN_PROGRESS = true;

                try {
                    this.showOptimisticCartUpdate(productData, formData, slide);
                } catch (e) {
                    console.error('Optimistic update failed:', e);
                }

                if (slide && optimisticHandles.length && isSyncedHost) {
                    optimisticHandles.forEach(h => UpsellProducts.optimisticAddedHandles.add(h));
                    this.filterSlidesByCart();
                }

                const gwpInput = form.querySelector('[name="gwp-upsell"]');
                let fetchBody = formData;
                let fetchHeaders = { 'X-Requested-With': 'XMLHttpRequest' };

                if (gwpInput?.dataset?.variantId) {
                    const mainVariantId = formData.get('id');
                    const sellingPlanValue = formData.get('selling_plan');
                    const mainProperties = {};
                    for (const [k, v] of formData.entries()) {
                        const m = k.match(/^properties\[(.+)\]$/);
                        if (m) mainProperties[m[1]] = v;
                    }

                    const gwpVariantId = gwpInput.dataset.variantId;
                    const gwpProductType = (gwpInput.dataset.productType || '').trim().toLowerCase();
                    const gwpLessonsCount = (gwpInput.dataset.lessonsCount || '').trim();
                    const gwpProperties = {
                        '_gwp-product': 'true',
                        '_gwp-product-for': String(mainVariantId),
                    };
                    if (gwpProductType === 'lesson' || gwpLessonsCount) {
                        gwpProperties['_lessons'] = 'true';
                        if (gwpLessonsCount) gwpProperties['_lessons_count'] = gwpLessonsCount;
                    }
                    if (igItemProperty) {
                        const [igKey, igVal] = Object.entries(igItemProperty)[0];
                        gwpProperties[igKey] = igVal;
                    }

                    // Link main item to its GWP — mirrors product-form.js lines 665/696
                    mainProperties['_gwp-product-related'] = gwpVariantId;

                    const sectionIds = cartDrawer?.getSectionsToRender?.().map(s => s.section || s.id) || [];
                    const mainItem = { id: parseInt(mainVariantId), quantity: 1, properties: mainProperties };
                    if (sellingPlanValue) mainItem.selling_plan = parseInt(sellingPlanValue);

                    fetchBody = JSON.stringify({
                        items: [mainItem, { id: parseInt(gwpVariantId), quantity: 1, properties: gwpProperties }],
                        sections: sectionIds,
                        sections_url: window.location.pathname,
                    });
                    fetchHeaders['Content-Type'] = 'application/json';
                }

                const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
                    method: 'POST',
                    body: fetchBody,
                    headers: fetchHeaders,
                });

                if (!response.ok) throw new Error(`Cart API error: ${response.status}`);

                const data = await response.json();

                window._CART_REQUESTS--;
                if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
                    await cartDrawer.renderContents(data, 'add-to-cart-form');
                }

                if (typeof updateCartAndProductJSON === 'function') await updateCartAndProductJSON();

                window._CART_ADD_IN_PROGRESS = false;
                button.classList.remove('is-added');
                button.disabled = false;
                if (label) label.textContent = 'Add';
            } catch (error) {
                window._CART_ADD_IN_PROGRESS = false;
                console.error('Error adding upsell to cart:', error);
                this.revertOptimisticUpdate(productData);
                button.classList.remove('is-added');
                button.disabled = false;
                if (label) label.textContent = 'Add';
                if (slide && optimisticHandles.length && isSyncedHost) {
                    optimisticHandles.forEach(h => UpsellProducts.optimisticAddedHandles.delete(h));
                    this.filterSlidesByCart();
                }
                window._CART_REQUESTS--;
            }
        }

        hasSubscriptionInCart() {
            const cart = window._cartState;
            if (!cart?.items) return false;
            const [propertyKey, propertyValue] = Object.entries(SubscriptionServiceManager.subscriptionPrepaidPropertyMain)[0];
            return cart.items.some(item => {
                const isSubscriptionProduct = item.product_type === window.cartStrings?.subscriptionProductType;
                const isSellingPlanAllocation = !!item.selling_plan_allocation;
                const isPrepaidPackage = item.properties?.[propertyKey] === propertyValue;
                return isSubscriptionProduct && isSellingPlanAllocation && !isPrepaidPackage;
            });
        }

        showOptimisticCartUpdate(productData, formData, slide) {
            const cartDrawer = document.querySelector('cart-drawer');
            if (!cartDrawer) return;

            cartDrawer.classList.remove('is-empty');
            const drawerItems = cartDrawer.querySelector('cart-drawer-items');
            if (drawerItems) {
                drawerItems.classList.remove('is-empty');
                drawerItems.style.display = '';  // clear forced display:none from _maybeHideItemsForEmpty
            }
            const drawerInner = cartDrawer.querySelector('.drawer__inner');
            if (drawerInner) drawerInner.classList.remove('is-empty');

            const checkoutBtn = cartDrawer.querySelector('#CartDrawer-Checkout');
            if (checkoutBtn) checkoutBtn.removeAttribute('disabled');

            const container = cartDrawer.querySelector('#CartDrawer-CartItems .drawer__cart-items-wrapper [role="rowgroup"]');
            if (!container) return;

            const formItems = parseFormDataItems(formData);
            const productFormItem = getFormItem(productData, formItems);
            const existingCartItem = getExistingCartItem(productFormItem);

            if (existingCartItem) {
                updateOptimisticCartItemQuantity(existingCartItem, productFormItem.quantity);
            } else {
                const optimisticItem = this.createOptimisticCartItem(productData);
                if (optimisticItem) {
                    container.prepend(optimisticItem);

                    // Insert optimistic GWP item right after the main optimistic item
                    const gwpInput = slide?.querySelector('[name="gwp-upsell"]');
                    if (gwpInput?.dataset?.variantId) {
                        optimisticItem.classList.add('cart-item--gwp-related');
                        const optimisticGwp = this.createOptimisticGwpItem(gwpInput);
                        if (optimisticGwp) optimisticItem.after(optimisticGwp);
                    }

                    const newHandle = slide?.dataset?.productHandle;
                    const existingHost = container.querySelector('[data-cpg-host="true"]');
                    if (newHandle && existingHost && existingHost.dataset.productHandle === newHandle) {
                        // Move cart-progress-gift right after the new optimistic item
                        const cpgItem = container.querySelector('.cart-item--cart-progress-gift');
                        if (cpgItem) optimisticItem.after(cpgItem);
                        // Move any fathers-day-offer--cart that belongs to a duplicate
                        // of the same product to also appear after the new optimistic item
                        container.querySelectorAll('.fathers-day-offer--cart').forEach(fdo => {
                            let prev = fdo.previousElementSibling;
                            while (prev && !prev.classList.contains('cart-item')) {
                                prev = prev.previousElementSibling;
                            }
                            if (prev && prev.dataset.productHandle === newHandle && !prev.hasAttribute('data-cpg-host')) {
                                optimisticItem.after(fdo);
                            }
                        });
                    }
                }
            }

            if (productData && productData.price && window.CartOptimist) {
                CartOptimist.optimisticAdd(productData.price, productData.compareAtPrice || 0, 0);
            }

        }

        createOptimisticGwpItem(gwpInput) {
            const title    = gwpInput.dataset.title || '';
            const image    = gwpInput.dataset.image || '';
            const image2x  = gwpInput.dataset.image2x || '';
            const subtitle = gwpInput.dataset.subtitle || '';
            if (!title && !image) return null;

            const imgTag = image
                ? `<img class="cart-item--gwp__image" src="${image}" srcset="${image} 1x${image2x ? `, ${image2x} 2x` : ''}" alt="${title}" width="24" height="24" />`
                : '';
            const subtitleTag = subtitle
                ? `<span class="cart-item--gwp__subtitle">${subtitle}</span>`
                : '';

            const el = document.createElement('div');
            el.className = 'cart-item cart-item--gwp cart-item--optimistic-gwp';
            el.setAttribute('role', 'row');
            el.innerHTML = `
                <div class="cart-item--gwp__inner">
                    <div class="cart-item--gwp__image-wrap">
                        ${imgTag}
                        <span class="cart-item--gwp__qty js-cart-item-gwp-quantity" aria-label="Quantity: 1">1</span>
                    </div>
                    <span class="cart-item--gwp__text">
                        <strong>${title}</strong>
                        ${subtitleTag}
                    </span>
                    <span class="cart-item--gwp__badge">Included</span>
                </div>`;
            return el;
        }

        revertOptimisticUpdate(productData) {
            const cartDrawer = document.querySelector('cart-drawer');
            if (!cartDrawer) return;

            cartDrawer.querySelectorAll('.cart-item--optimistic, .cart-item--optimistic-gwp').forEach(el => el.remove());

            revertOptimisticCartItemQuantity();

            if (window.CartOptimist) CartOptimist.revert();

            const hasRealItems = !!cartDrawer.querySelector('[role="rowgroup"] .cart-item:not(.cart-item--optimistic)');
            if (!hasRealItems) {
                cartDrawer.classList.add('is-empty');
            }
        }

        getProductDataFromSlide(slide) {
            if (!slide) return null;

            const img = slide.querySelector('.upsell-product__image');
            const titleEl = slide.querySelector('.upsell-product__heading');
            const isOnSale = slide.querySelector('.price--on-sale');
            const variantId = slide.querySelector('form [name="id"]')?.value;

            let price = 0;
            let compareAtPrice = 0;
            let sellingPlanName = '';
            let priceSourceEl = null;

            if (isOnSale) {
                const salePriceEl = slide.querySelector('.price__sale .price-item--sale');
                const comparePriceEl = slide.querySelector('.price__sale s.price-item--regular');
                price = this.parsePriceFromDOM(salePriceEl);
                compareAtPrice = this.parsePriceFromDOM(comparePriceEl);
                priceSourceEl = salePriceEl;
            } else {
                const regularPriceEl = slide.querySelector('.price__regular .price-item--regular');
                price = this.parsePriceFromDOM(regularPriceEl);
                priceSourceEl = regularPriceEl;
            }

            if (priceSourceEl) {
                const priceText = priceSourceEl.textContent.trim();
                const slashIdx = priceText.indexOf('/');
                if (slashIdx !== -1) sellingPlanName = priceText.substring(slashIdx + 1).trim();
            }

            const sellingPlanInput = slide.querySelector('form [name="selling_plan"]');

            return {
                title: titleEl?.textContent?.trim() || 'Product',
                image: img?.src || '',
                price: price,
                compareAtPrice: compareAtPrice,
                sellingPlanName: sellingPlanName,
                isSubscription: !!(sellingPlanInput?.value),
                url: '#',
                variantId: variantId || ''
            };
        }

        parsePriceFromDOM(el) {
            if (!el) return 0;
            const text = el.textContent.replace(/[^0-9.]/g, '');
            return Math.round(parseFloat(text) * 100) || 0;
        }

        createOptimisticCartItem(productData) {
            const item = document.createElement('div');
            item.setAttribute('role', 'row');
            const isCartRedesign = document.querySelector(UpsellProducts.selectors.cartRedesign);

            if (!productData) {
                item.className = 'cart-item cart-item--skeleton';
                item.innerHTML = `
                    <div class="cart-item__media skeleton-pulse"></div>
                    <div class="cart-item__content-wrapper">
                        <div class="skeleton-content">
                            <div class="skeleton-line skeleton-pulse" style="width:60%;height:14px"></div>
                            <div class="skeleton-line skeleton-pulse" style="width:80%;height:20px;margin-top:10px"></div>
                            <div class="skeleton-line skeleton-pulse" style="width:100px;height:34px;margin-top:12px;border-radius:60px"></div>
                        </div>
                    </div>`;
                return item;
            }

            const priceFormatted = typeof formatMoneyWithoutTrailingZeros === 'function'
                ? formatMoneyWithoutTrailingZeros(productData.price)
                : formatMoney(productData.price, '${{amount}}');
            const planSuffix = productData.sellingPlanName ? ` / ${productData.sellingPlanName}` : '';
            const imgSrc = productData.image
                ? productData.image.split('?')[0] + '?width=200&height=200&crop=center'
                : '';

            let priceHTML = '';
            if (productData.compareAtPrice && productData.compareAtPrice > productData.price) {
                const compareFormatted = typeof formatMoneyWithoutTrailingZeros === 'function'
                    ? formatMoneyWithoutTrailingZeros(productData.compareAtPrice)
                    : formatMoney(productData.compareAtPrice, '${{amount}}');
                const compareAtTemplate = window?.cartStrings?.compareAtPriceValue || '[price]';
                const compareAtText = compareAtTemplate.replace('[price]', compareFormatted);
                const savingsSuffix = window?.cartStrings?.savingsBadgeSuffix || 'OFF';
                const savingsPercent = Math.round(((productData.compareAtPrice - productData.price) / productData.compareAtPrice) * 100);
                priceHTML = `
                    <div class="cart-item__discounted-prices">
                        <strong class="cart-item__final-price cart-item__price">${priceFormatted}${planSuffix}</strong>
                        <s class="cart-item__old-price cart-item__price">${compareAtText}</s>
                        ${!isCartRedesign ? '<span class="cart-item__final-price-badge badge price__badge-sale">${savingsPercent}% ${savingsSuffix}</span>' : ''}
                    </div>`;
            } else {
                priceHTML = `<div class="cart-item__price">${priceFormatted}${planSuffix}</div>`;
            }

            const quantityControlsHTML = productData.isSubscription
                ? `<input class="js-cart-item-quantity" type="hidden" name="updates[]" value="1" />`
                : `
                    <quantity-input class="quantity">
                        <button class="quantity__button no-js-hidden" name="minus" type="button">
                            <span class="visually-hidden">Decrease quantity</span>
                            <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" fill="none" viewBox="0 0 10 2">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"></path>
                            </svg>
                        </button>
                        <input class="quantity__input" type="number" value="1" min="0" aria-label="Quantity" />
                        <button class="quantity__button no-js-hidden" name="plus" type="button">
                            <span class="visually-hidden">Increase quantity</span>
                            <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" fill="none" viewBox="0 0 10 10">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"></path>
                            </svg>
                        </button>
                    </quantity-input>`;

            const removeButtonHTML = `
                <cart-remove-button>
                    <button class="button button--tertiary" type="button" aria-label="Remove ${productData.title}">
                        <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M14 3.98665C11.78 3.76665 9.54667 3.65332 7.32 3.65332C6 3.65332 4.68 3.71999 3.36 3.85332L2 3.98665" stroke="#6D6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M5.66675 3.31325L5.81341 2.43992C5.92008 1.80659 6.00008 1.33325 7.12675 1.33325H8.87341C10.0001 1.33325 10.0867 1.83325 10.1867 2.44659L10.3334 3.31325" stroke="#6D6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M12.5667 6.09326L12.1334 12.8066C12.06 13.8533 12.0001 14.6666 10.14 14.6666H5.86002C4.00002 14.6666 3.94002 13.8533 3.86668 12.8066L3.43335 6.09326" stroke="#6D6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M6.88672 11H9.10672" stroke="#6D6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M6.33325 8.33325H9.66659" stroke="#6D6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </cart-remove-button>
            `;

            item.className = 'cart-item cart-item--optimistic';
            item.innerHTML = `
                <div class="cart-item__media">
                    ${imgSrc ? `<img class="cart-item__image" src="${imgSrc}" alt="${productData.title}" width="100" height="100" style="object-fit:cover" />` : ''}
                    <a class="cart-item__link" href="${productData.url}" aria-label="${productData.title}"></a>
                </div>
                <div class="cart-item__content-wrapper">
                    <div class="cart-item__details">
                        <a class="cart-item__name cart-item__name--link h4 break" href="${productData.url}">${productData.title}</a>
                        
                        ${isCartRedesign ? removeButtonHTML : ''}
                    </div>
                    <div class="cart-item__quantity">
                        ${!isCartRedesign ? priceHTML : ''}
                        <div class="cart-item__quantity-wrapper">
                            ${quantityControlsHTML}
                            
                            ${isCartRedesign ? priceHTML : ''}
                        </div>
                    </div>
                </div>
                ${!isCartRedesign ? removeButtonHTML : ''}`;
            return item;
        }
    });
}
